import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { PermissionSet, useBusinessContext, usePermission } from '@root/context'
import { fetchBankIntegrations, IBankIntegration } from '@root/query'
import { fetchHolviAccounts, IHolviAccount } from '@root/query/holvi'

const BANK_SYNC_STALE_HOURS = 24

export type BankMonitoringVariant = 'missing' | 'stale'

const isSyncFresh = (lastSyncAt?: string | null) =>
  Boolean(lastSyncAt) && dayjs().diff(dayjs(lastSyncAt), 'hour') < BANK_SYNC_STALE_HOURS

export const bankMonitoringStatus = (
  bankIntegrations: IBankIntegration[] | undefined,
  holviAccounts: IHolviAccount[] | undefined
) => {
  const enabledBankAccounts = (bankIntegrations || []).flatMap(integration =>
    (integration.accounts || []).filter(account => account.enabled)
  )
  const enabledHolvi = (holviAccounts || []).filter(account => account.enabled)
  const hasIntegration = enabledBankAccounts.length > 0 || enabledHolvi.length > 0
  if (!hasIntegration) {
    return { hasIntegration: false, isFresh: false }
  }
  const anyStaledIntegration = (bankIntegrations || []).some(
    integration => integration.enabled && integration.is_staled
  )
  const anyFreshAccount = [...enabledBankAccounts, ...enabledHolvi].some(account =>
    isSyncFresh(account.last_sync_at)
  )
  return { hasIntegration: true, isFresh: !anyStaledIntegration && anyFreshAccount }
}

export const useBankMonitoringStatus = () => {
  const { slug, data: business } = useBusinessContext()
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)

  const { data: bankIntegrationsData, isLoading: isBankIntegrationsLoading } = useQuery({
    queryKey: [slug, 'bank_integrations'],
    queryFn: () => fetchBankIntegrations({ slug }),
    enabled: canManageIntegrations
  })

  const { data: holviAccountsData, isLoading: isHolviAccountsLoading } = useQuery({
    queryKey: [slug, 'holvi', 'accounts'],
    queryFn: () => fetchHolviAccounts({ slug }),
    enabled: canManageIntegrations && Boolean(business?.holvi_bookkeeping_api_enabled)
  })

  const isLoading =
    canManageIntegrations &&
    (isBankIntegrationsLoading ||
      (Boolean(business?.holvi_bookkeeping_api_enabled) && isHolviAccountsLoading))

  if (!canManageIntegrations || isLoading) {
    return { isLoading, variant: null as BankMonitoringVariant | null }
  }

  const { hasIntegration, isFresh } = bankMonitoringStatus(
    bankIntegrationsData?.results,
    holviAccountsData?.results
  )

  if (!hasIntegration) return { isLoading: false, variant: 'missing' as const }
  if (!isFresh) return { isLoading: false, variant: 'stale' as const }
  return { isLoading: false, variant: null as BankMonitoringVariant | null }
}
